import Chart from 'chart.js/auto';

const formatMinutes = (minutes) => {
    const safeMinutes = Number.isFinite(minutes) ? Math.max(0, Math.round(minutes)) : 0;
    const hours = Math.floor(safeMinutes / 60);
    const remainingMinutes = safeMinutes % 60;

    if (hours === 0) {
        return `${remainingMinutes}分`;
    }

    if (remainingMinutes === 0) {
        return `${hours}時間`;
    }

    return `${hours}時間${remainingMinutes}分`;
};

const formatAverage = (value) => {
    if (!Number.isFinite(value)) {
        return '0分';
    }

    return Number.isInteger(value) ? `${value}分` : `${value.toFixed(1)}分`;
};

const formatPercentage = (value) => {
    if (!Number.isFinite(value)) {
        return '―';
    }

    return Number.isInteger(value) ? `${value}%` : `${value.toFixed(1)}%`;
};

const formatSignedPercentage = (value) => {
    if (!Number.isFinite(value)) {
        return '―';
    }

    const rounded = Number.isInteger(value) ? value : Number(value).toFixed(1);
    if (Number(value) > 0) {
        return `+${rounded}%`;
    }
    if (Number(value) < 0) {
        return `${rounded}%`;
    }
    return '±0%';
};

const formatSignedMinutes = (minutes) => {
    if (!Number.isFinite(minutes)) {
        return '―';
    }

    const safeMinutes = Math.round(minutes);
    const absMinutes = Math.abs(safeMinutes);
    const sign = safeMinutes > 0 ? '+' : safeMinutes < 0 ? '-' : '±';

    if (absMinutes >= 60) {
        const hours = Math.floor(absMinutes / 60);
        const remain = absMinutes % 60;
        if (remain === 0) {
            return `${sign}${hours}時間`;
        }
        return `${sign}${hours}時間${remain}分`;
    }

    return `${sign}${absMinutes}分`;
};

const formatPercent = (value, fractionDigits = 1) => {
    const numeric = Number.isFinite(Number(value)) ? Number(value) : 0;
    return `${numeric.toFixed(fractionDigits)}%`;
};

const formatNotificationDate = (value) => {
    if (!value) {
        return '―';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}年${month}月${day}日`;
};

const normalizeDateKey = (value) => {
    if (!value && value !== 0) {
        return '';
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    const stringValue = String(value).trim();
    if (!stringValue) {
        return '';
    }

    const datePart = stringValue.split(/[T\s]/)[0].replace(/\//g, '-');
    const [year = '', month = '', day = ''] = datePart.split('-');
    if (!year || !month || !day) {
        return datePart;
    }

    return `${year.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};


export default class LearningTimeApp {
    constructor() {
        this.sidebar = document.getElementById('learning-time-sidebar');
        this.toggleButton = document.getElementById('learning-time-toggle');
        this.progressSection = document.querySelector('[data-role="progress-section"]');
        this.isViewOnly = !this.sidebar || !this.toggleButton;

        this.userId = this.resolveUserId();
        this.dismissedAlertsStorageKey = this.buildDismissedAlertsStorageKey();
        this.dismissedAlertKeys = this.loadDismissedAlertKeys();

        if (!this.progressSection) {
            return;
        }

        this.progressCourseName = null;
        this.progressRateLabel = null;
        this.progressTotalLabel = null;
        this.progressDailyLabel = null;
        this.progressWeeklyLabel = null;
        this.progressWeeklyPlanLabel = null;
        this.progressWeeklyDiffLabel = null;
        this.progressWeeklyMessage = null;
        this.progressExpectedProgressLabel = null;
        this.progressDiffLabel = null;
        this.progressGapMessage = null;
        this.progressEmptyMessage = null;
        this.progressRecentList = null;
        this.progressPrevButton = null;
        this.progressNextButton = null;
        this.progressCircleCanvas = null;
        this.progressHistoryCanvas = null;
        this.progressCalendarContainer = null;
        this.progressCalendarLabel = null;
        this.progressCalendarPrevButton = null;
        this.progressCalendarNextButton = null;
        this.progressStreakContainer = null;
        this.progressViewButtons = [];
        this.progressPanels = [];
        this.activeProgressView = 'summary';
        this.sideViewButtons = [];
        this.sidePanels = [];
        this.progressAlertsContainer = null;
        this.progressAlertsList = null;
        this.progressAlertsEmpty = null;
        this.weeklyActualLabel = null;
        this.weeklyGapLabel = null;
        this.weeklyTrendContainer = null;
        this.weeklyTrendIcon = null;
        this.weeklyTrendLabel = null;
        this.progressAlertsInfo = null;
        this.activeSideView = 'recent';
        this.calendarStates = new Map();

        if (this.progressSection) {
            this.progressCourseName = this.progressSection.querySelector('[data-role="progress-course-name"]');
            this.progressRateLabel = this.progressSection.querySelector('[data-role="progress-rate"]');
            this.progressTotalLabel = this.progressSection.querySelector('[data-role="total-minutes"]');
            this.progressDailyLabel = this.progressSection.querySelector('[data-role="daily-average"]');
            this.progressWeeklyLabel = this.progressSection.querySelector('[data-role="weekly-average"]');
            this.progressWeeklyPlanLabel = this.progressSection.querySelector('[data-role="weekly-plan-minutes"]');
            this.progressWeeklyDiffLabel = this.progressSection.querySelector('[data-role="weekly-diff-minutes"]');
            this.progressWeeklyMessage = this.progressSection.querySelector('[data-role="weekly-goal-message"]');
            this.progressExpectedProgressLabel = this.progressSection.querySelector('[data-role="expected-progress-rate"]');
            this.progressDiffLabel = this.progressSection.querySelector('[data-role="progress-diff-rate"]');
            this.progressGapMessage = this.progressSection.querySelector('[data-role="progress-gap-message"]');
            this.progressEmptyMessage = this.progressSection.querySelector('[data-role="progress-empty"]');
            this.progressRecentList = this.progressSection.querySelector('[data-role="progress-recent-list"]');
            this.progressPrevButton = this.progressSection.querySelector('[data-role="progress-prev"]');
            this.progressNextButton = this.progressSection.querySelector('[data-role="progress-next"]');
            this.progressCircleCanvas = this.progressSection.querySelector('[data-role="progress-circle"]');
            this.progressHistoryCanvas = this.progressSection.querySelector('[data-role="progress-chart"]');
            this.progressCalendarContainer = this.progressSection.querySelector('[data-role="progress-calendar"]');
            this.progressCalendarLabel = this.progressSection.querySelector('[data-role="calendar-label"]');
            this.progressCalendarPrevButton = this.progressSection.querySelector('[data-role="calendar-prev"]');
            this.progressCalendarNextButton = this.progressSection.querySelector('[data-role="calendar-next"]');
            this.progressStreakContainer = this.progressSection.querySelector('[data-role="progress-streak"]');
            this.progressViewButtons = Array.from(this.progressSection.querySelectorAll('[data-role="progress-view-button"]'));
            this.progressPanels = Array.from(this.progressSection.querySelectorAll('[data-role="progress-panel"]'));
            this.sideViewButtons = Array.from(this.progressSection.querySelectorAll('[data-role="side-view-button"]'));
            this.sidePanels = Array.from(this.progressSection.querySelectorAll('[data-role="side-panel"]'));
            this.progressAlertsContainer = this.progressSection.querySelector('[data-role="progress-alerts"]');
            this.progressAlertsList = this.progressSection.querySelector('[data-role="progress-alerts-list"]');
            this.progressAlertsEmpty = this.progressSection.querySelector('[data-role="progress-alerts-empty"]');
            this.progressAlertsInfo = this.progressSection.querySelector('[data-role="progress-alerts-info"]');
            this.weeklyActualLabel = this.progressSection.querySelector('[data-role="weekly-actual-value"]');
            this.weeklyGapLabel = this.progressSection.querySelector('[data-role="weekly-gap-value"]');
            this.weeklyTrendContainer = this.progressSection.querySelector('[data-role="weekly-trend"]');
            this.weeklyTrendIcon = this.progressSection.querySelector('[data-role="weekly-trend-icon"]');
            this.weeklyTrendLabel = this.progressSection.querySelector('[data-role="weekly-trend-label"]');
        }

        this.progressCircleChart = null;
        this.progressHistoryChart = null;

        this.closeButtons = Array.from(this.sidebar ? this.sidebar.querySelectorAll('[data-role="close"]') : []);
        this.form = this.sidebar ? this.sidebar.querySelector('#learning-time-form') : null;
        this.courseSelect = this.sidebar ? this.sidebar.querySelector('select[name="course_id"]') : null;
        this.chapterSelect = this.sidebar ? this.sidebar.querySelector('select[name="chapter_id"]') : null;
        this.textSelect = this.sidebar ? this.sidebar.querySelector('select[name="text_id"]') : null;
        this.dateInput = this.sidebar ? this.sidebar.querySelector('input[name="studied_at"]') : null;
        this.minutesInput = this.sidebar ? this.sidebar.querySelector('input[name="minutes"]') : null;
        this.memoInput = this.sidebar ? this.sidebar.querySelector('textarea[name="memo"]') : null;
        this.messageArea = this.sidebar ? this.sidebar.querySelector('[data-role="message"]') : null;

        const datasetSource = {
            ...(this.toggleButton && this.toggleButton.dataset ? this.toggleButton.dataset : {}),
            ...(this.progressSection ? this.progressSection.dataset : {}),
        };

        this.endpoints = {
            options: datasetSource.optionsEndpoint,
            summary: datasetSource.summaryEndpoint,
            store: datasetSource.storeEndpoint,
            dismiss: datasetSource.dismissEndpoint,
        };

        this.courseOptions = [];
        this.courseSummaries = [];
        this.activeCourseIndex = 0;
        this.initialized = false;
        this.loading = false;

        this.registerEvents();
        this.setProgressView(this.activeProgressView);
        this.setSideView(this.activeSideView);
        this.clearCalendarDisplay();
        this.setDefaultDate();
        // 初期表示で進捗サマリを表示
        if (this.progressSection) {
            this.fetchProgressSummary();
        }
    }

    async fetchProgressSummary() {
        if (!this.endpoints.summary) return;
        if (this.progressCourseName) {
            this.progressCourseName.textContent = 'コースを読み込み中...';
        }
        try {
            const res = await window.axios.get(this.endpoints.summary);
            this.courseSummaries = res.data?.courses || [];
            this.activeCourseIndex = 0;
            this.renderActiveCourse();
        } catch (e) {
            console.error(e);
            if (this.progressCourseName) this.progressCourseName.textContent = '学習時間の取得に失敗しました';
        }
    }

    registerEvents() {
        if (this.toggleButton) {
            this.toggleButton.addEventListener('click', () => this.openSidebar());
        }

        this.closeButtons.forEach((button) => {
            button.addEventListener('click', () => this.closeSidebar());
        });

        if (this.progressPrevButton) {
            this.progressPrevButton.addEventListener('click', () => this.changeCourse(-1));
        }

        if (this.progressNextButton) {
            this.progressNextButton.addEventListener('click', () => this.changeCourse(1));
        }

        if (this.courseSelect) {
            this.courseSelect.addEventListener('change', () => this.handleCourseSelectChange());
        }

        if (this.chapterSelect) {
            this.chapterSelect.addEventListener('change', () => this.handleChapterSelectChange());
        }

        if (this.form) {
            this.form.addEventListener('submit', (event) => this.handleSubmit(event));
        }

        if (this.progressCalendarPrevButton) {
            this.progressCalendarPrevButton.addEventListener('click', () => this.changeCalendarMonth(-1));
        }

        if (this.progressCalendarNextButton) {
            this.progressCalendarNextButton.addEventListener('click', () => this.changeCalendarMonth(1));
        }

        this.sideViewButtons.forEach((button) => {
            button.addEventListener('click', () => {
                const { view } = button.dataset;
                this.setSideView(view || 'recent');
            });
        });

        if (this.progressAlertsInfo) {
            this.progressAlertsInfo.addEventListener('focus', () => {
                if (this.progressAlertsContainer) {
                    this.progressAlertsContainer.classList.add('show-tooltip');
                }
            });
            this.progressAlertsInfo.addEventListener('blur', () => {
                if (this.progressAlertsContainer) {
                    this.progressAlertsContainer.classList.remove('show-tooltip');
                }
            });
        }

        this.progressViewButtons.forEach((button) => {
            button.addEventListener('click', () => {
                const { view } = button.dataset;
                if (view) {
                    this.setProgressView(view);
                }
            });
        });
    }

    setDefaultDate() {
        if (!this.dateInput) {
            return;
        }
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = (`0${today.getMonth() + 1}`).slice(-2);
        const dd = (`0${today.getDate()}`).slice(-2);
        this.dateInput.value = `${yyyy}-${mm}-${dd}`;
    }

    openSidebar() {
        if (!this.sidebar) {
            return;
        }

        this.sidebar.classList.add('is-open');

        if (!this.initialized && !this.loading) {
            this.initialize();
        }
    }

    closeSidebar() {
        if (this.sidebar) {
            this.sidebar.classList.remove('is-open');
        }
    }

    async initialize() {
        this.loading = true;
        this.showMessage('読み込み中です...', 'info');

        try {
            const [optionsResponse, summaryResponse] = await Promise.all([
                window.axios.get(this.endpoints.options),
                window.axios.get(this.endpoints.summary),
            ]);

            this.courseOptions = optionsResponse.data.courses || [];
            this.courseSummaries = summaryResponse.data.courses || [];

            this.populateCourseSelect();
            if (this.progressSection) {
                this.renderActiveCourse();
            }

            this.initialized = true;
            this.hideMessage();
        } catch (error) {
            console.error(error);
            this.showMessage('学習時間の情報取得に失敗しました。時間をおいて再度お試しください。', 'error');
        } finally {
            this.loading = false;
        }
    }

    populateCourseSelect() {
        if (!this.courseSelect) {
            return;
        }

        this.courseSelect.innerHTML = '';

        if (!this.courseOptions.length) {
            const option = document.createElement('option');
            option.textContent = '受講中のコースがありません';
            option.disabled = true;
            option.selected = true;
            this.courseSelect.appendChild(option);
            this.courseSelect.disabled = true;

            this.chapterSelect.innerHTML = '';
            this.chapterSelect.disabled = true;
            this.textSelect.innerHTML = '';
            this.textSelect.disabled = true;
            return;
        }

        this.courseOptions.forEach((course, index) => {
            const option = document.createElement('option');
            option.value = course.id;
            option.textContent = course.name;
            if (index === 0) {
                option.selected = true;
            }
            this.courseSelect.appendChild(option);
        });

        this.courseSelect.disabled = false;
        this.handleCourseSelectChange();
    }

    handleCourseSelectChange() {
        const courseId = Number(this.courseSelect.value);
        const selectedCourse = this.courseOptions.find((course) => course.id === courseId);

        if (!selectedCourse) {
            this.chapterSelect.innerHTML = '';
            this.textSelect.innerHTML = '';
            return;
        }

        this.populateChapterSelect(selectedCourse);
        this.syncSummaryIndex(courseId);
        this.renderActiveCourse();
    }

    populateChapterSelect(course) {
        this.chapterSelect.innerHTML = '';

        if (!course.chapters.length) {
            const option = document.createElement('option');
            option.textContent = '章が登録されていません';
            option.disabled = true;
            option.selected = true;
            this.chapterSelect.appendChild(option);
            this.chapterSelect.disabled = true;

            this.textSelect.innerHTML = '';
            this.textSelect.disabled = true;
            return;
        }

        course.chapters.forEach((chapter, index) => {
            const option = document.createElement('option');
            option.value = chapter.id;
            option.textContent = chapter.name;
            if (index === 0) {
                option.selected = true;
            }
            this.chapterSelect.appendChild(option);
        });

        this.chapterSelect.disabled = false;
        this.handleChapterSelectChange();
    }

    handleChapterSelectChange() {
        const courseId = Number(this.courseSelect.value);
        const chapterId = Number(this.chapterSelect.value);
        const selectedCourse = this.courseOptions.find((course) => course.id === courseId);

        if (!selectedCourse) {
            return;
        }

        const selectedChapter = selectedCourse.chapters.find((chapter) => chapter.id === chapterId);
        this.populateTextSelect(selectedChapter);
    }

    populateTextSelect(chapter) {
        this.textSelect.innerHTML = '';

        if (!chapter || !chapter.texts.length) {
            const option = document.createElement('option');
            option.textContent = 'テキストが登録されていません';
            option.disabled = true;
            option.selected = true;
            this.textSelect.appendChild(option);
            this.textSelect.disabled = true;
            return;
        }

        chapter.texts.forEach((text, index) => {
            const option = document.createElement('option');
            option.value = text.id;
            option.textContent = text.name;
            if (index === 0) {
                option.selected = true;
            }
            this.textSelect.appendChild(option);
        });

        this.textSelect.disabled = false;
    }

    changeCourse(delta) {
        if (!this.courseSummaries.length) {
            return;
        }

        const nextIndex = this.activeCourseIndex + delta;
        if (nextIndex < 0 || nextIndex >= this.courseSummaries.length) {
            return;
        }

        this.activeCourseIndex = nextIndex;
        this.renderActiveCourse();
        const activeSummary = this.courseSummaries[this.activeCourseIndex];
        if (this.courseSelect) {
            this.courseSelect.value = String(activeSummary.course_id);
            this.handleCourseSelectChange();
        }
    }

    syncSummaryIndex(courseId) {
        const index = this.courseSummaries.findIndex((course) => course.course_id === courseId);
        if (index >= 0) {
            this.activeCourseIndex = index;
        }
    }

    renderActiveCourse() {
        if (!this.progressCourseName) {
            return;
        }

        if (!this.courseSummaries.length) {
            this.progressCourseName.textContent = '受講中のコースがありません';
            this.updateProgressControls();
            this.setProgressEmptyState(true);
            this.updateProgressCircleChart(0);
            this.updateProgressHistoryChart([]);
            this.progressTotalLabel.textContent = '0分';
            this.progressDailyLabel.textContent = '0分';
            this.progressWeeklyLabel.textContent = '0分';
            this.setProgressView('summary');
            this.progressViewButtons.forEach((button) => {
                button.disabled = true;
            });
            this.sideViewButtons.forEach((button) => {
                button.disabled = true;
            });
            this.clearCalendarDisplay();
            this.calendarStates.clear();
            this.updateScheduleInsights({
                weekly_plan_minutes: null,
                current_week_minutes: 0,
                expected_progress_rate: null,
                progress_rate: 0,
            });
            this.updateWeeklyHighlights(null);
            this.renderUnreportedAlerts([]);
            this.setSideView('recent');
            return;
        }

        const summary = this.courseSummaries[this.activeCourseIndex];
        if (!summary) {
            return;
        }

        this.progressViewButtons.forEach((button) => {
            button.disabled = false;
        });
        this.sideViewButtons.forEach((button) => {
            button.disabled = false;
        });

        this.progressCourseName.textContent = summary.course_name || '';
        this.progressRateLabel.textContent = `${Math.round(summary.progress_rate ?? 0)}%`;
        this.progressTotalLabel.textContent = formatMinutes(summary.total_minutes);
        this.progressDailyLabel.textContent = formatAverage(summary.daily_average_minutes);
        this.progressWeeklyLabel.textContent = formatAverage(summary.weekly_average_minutes);
        this.updateScheduleInsights(summary);
        this.updateWeeklyHighlights(summary);

        const recentLogs = summary.recent_logs || [];
        this.renderRecentLogs(recentLogs);
        this.setProgressEmptyState(recentLogs.length === 0 && summary.total_minutes === 0);

        this.updateProgressControls();
        this.updateProgressCircleChart(summary.progress_rate ?? 0);
        this.updateProgressHistoryChart(summary.last_seven_days || []);
        this.renderCalendar(summary);
        this.renderUnreportedAlerts(summary.unreported_logs || []);
        this.setSideView(this.activeSideView);
        this.setProgressView(this.activeProgressView);
    }

    updateScheduleInsights(summary) {
        const hasPlanMinutes = !!(summary && summary.weekly_plan_minutes !== undefined && summary.weekly_plan_minutes !== null);
        const weeklyPlanMinutes = hasPlanMinutes ? Math.max(0, Number(summary.weekly_plan_minutes) || 0) : null;
        const currentWeekMinutes = summary && summary.current_week_minutes !== undefined
            ? Math.max(0, Number(summary.current_week_minutes) || 0)
            : 0;
        const expectedProvided = summary
            && summary.expected_progress_rate !== undefined
            && summary.expected_progress_rate !== null
            && Number.isFinite(Number(summary.expected_progress_rate));
        const expectedProgress = expectedProvided ? Number(summary.expected_progress_rate) : null;
        const actualProgress = Number(summary && summary.progress_rate !== undefined ? summary.progress_rate : 0);
        const diff = expectedProgress !== null ? actualProgress - expectedProgress : null;
        const weeklyDiff = hasPlanMinutes ? currentWeekMinutes - weeklyPlanMinutes : null;

        if (this.progressWeeklyPlanLabel) {
            this.progressWeeklyPlanLabel.textContent = hasPlanMinutes ? formatMinutes(weeklyPlanMinutes) : '―';
        }

        if (this.progressWeeklyDiffLabel) {
            this.progressWeeklyDiffLabel.textContent = weeklyDiff !== null ? formatSignedMinutes(weeklyDiff) : '―';
        }

        if (this.progressWeeklyMessage) {
            this.progressWeeklyMessage.classList.remove('is-positive', 'is-warning', 'is-neutral');
            let weeklyMessage = '';
            let weeklyClass = 'is-neutral';

            if (!hasPlanMinutes) {
                weeklyMessage = '学習スケジュールが未設定です。';
            } else if (weeklyPlanMinutes === 0) {
                weeklyMessage = '週の必要時間は設定されていません。';
            } else if (weeklyDiff >= 0) {
                weeklyMessage = weeklyDiff > 0 ? '目標を上回る学習ができています。' : '今週の目標を達成しました！';
                weeklyClass = 'is-positive';
            } else {
                const remaining = Math.abs(weeklyDiff);
                weeklyMessage = `目標まであと${formatMinutes(remaining)}です。`;
                weeklyClass = 'is-warning';
            }

            this.progressWeeklyMessage.textContent = weeklyMessage;
            this.progressWeeklyMessage.classList.add(weeklyClass);
        }

        if (this.progressExpectedProgressLabel) {
            this.progressExpectedProgressLabel.textContent = expectedProgress !== null
                ? formatPercentage(expectedProgress)
                : '―';
        }

        if (this.progressDiffLabel) {
            this.progressDiffLabel.textContent = diff !== null ? formatSignedPercentage(diff) : '―';
        }

        if (this.progressGapMessage) {
            this.progressGapMessage.classList.remove('is-positive', 'is-warning', 'is-neutral');
            let progressMessage = '';
            let progressClass = 'is-neutral';

            if (!hasPlanMinutes) {
                progressMessage = '学習スケジュールが未設定のため比較できません。';
            } else if (expectedProgress === null) {
                progressMessage = '計画の進捗率を算出できません。';
            } else {
                const gap = actualProgress - expectedProgress;
                const absGap = Math.abs(gap);

                if (absGap < 0.5) {
                    progressMessage = '計画どおりに進んでいます。';
                } else if (gap > 0) {
                    const roundedGap = Math.round(absGap * 10) / 10;
                    progressMessage = `計画より${formatPercentage(roundedGap)}先行中です。`;
                    progressClass = 'is-positive';
                } else {
                    const roundedGap = Math.round(absGap * 10) / 10;
                    progressMessage = `計画より${formatPercentage(roundedGap)}遅れています。`;
                    progressClass = 'is-warning';
                }
            }

            this.progressGapMessage.textContent = progressMessage;
            this.progressGapMessage.classList.add(progressClass);
        }
    }

    updateWeeklyHighlights(summary) {
        if (!this.weeklyActualLabel || !this.weeklyGapLabel || !this.weeklyTrendLabel || !this.weeklyTrendIcon) {
            return;
        }

        if (!summary) {
            this.weeklyActualLabel.textContent = '0分';
            this.weeklyGapLabel.textContent = '予定通り';
            this.weeklyGapLabel.classList.remove('user-plan-diff--ahead', 'user-plan-diff--behind');
            this.weeklyGapLabel.classList.add('user-plan-diff--even');
            if (this.weeklyTrendContainer) {
                this.weeklyTrendContainer.classList.remove('user-progress-weekly-trend--up', 'user-progress-weekly-trend--down');
                this.weeklyTrendContainer.classList.add('user-progress-weekly-trend--flat');
            }
            this.weeklyTrendIcon.textContent = '▬';
            this.weeklyTrendLabel.textContent = '先週と同じペース（先週 0分）';
            return;
        }

        const actualWeekMinutes = Math.max(0, Number(summary.actual_week_minutes ?? summary.current_week_minutes ?? 0) || 0);
        const previousWeekMinutes = Math.max(0, Number(summary.previous_week_minutes ?? 0) || 0);
        const planMinutes = summary && summary.weekly_plan_minutes !== undefined && summary.weekly_plan_minutes !== null
            ? Math.max(0, Number(summary.weekly_plan_minutes) || 0)
            : null;
        const rawGap = summary && summary.weekly_gap_minutes !== undefined ? summary.weekly_gap_minutes : null;
        const weeklyGap = rawGap !== null && rawGap !== undefined ? Number(rawGap) : null;
        const weeklyTrend = summary.weekly_trend || 'flat';

        this.weeklyActualLabel.textContent = formatMinutes(actualWeekMinutes);

        this.weeklyGapLabel.classList.remove('user-plan-diff--ahead', 'user-plan-diff--behind', 'user-plan-diff--even');
        if (planMinutes === null || weeklyGap === null) {
            this.weeklyGapLabel.textContent = '学習スケジュールが未設定です。';
            this.weeklyGapLabel.classList.add('user-plan-diff--even');
        } else {
            let gapClass = 'user-plan-diff--even';
            let gapText = '予定通り';
            if (weeklyGap > 0) {
                gapClass = 'user-plan-diff--ahead';
                gapText = `+${formatMinutes(weeklyGap)}（先行）`;
            } else if (weeklyGap < 0) {
                gapClass = 'user-plan-diff--behind';
                gapText = `-${formatMinutes(Math.abs(weeklyGap))}（不足）`;
            }
            this.weeklyGapLabel.textContent = gapText;
            this.weeklyGapLabel.classList.add(gapClass);
        }

        if (this.weeklyTrendContainer) {
            this.weeklyTrendContainer.classList.remove('user-progress-weekly-trend--up', 'user-progress-weekly-trend--down', 'user-progress-weekly-trend--flat');
            const trendClass = weeklyTrend === 'up'
                ? 'user-progress-weekly-trend--up'
                : weeklyTrend === 'down'
                    ? 'user-progress-weekly-trend--down'
                    : 'user-progress-weekly-trend--flat';
            this.weeklyTrendContainer.classList.add(trendClass);
        }

        const trendIcon = weeklyTrend === 'up' ? '▲' : weeklyTrend === 'down' ? '▼' : '▬';
        this.weeklyTrendIcon.textContent = trendIcon;

        let trendText = '先週と同じペース';
        if (weeklyTrend === 'up') {
            trendText = '先週より増加';
        } else if (weeklyTrend === 'down') {
            trendText = '先週より減少';
        }
        this.weeklyTrendLabel.textContent = `${trendText}（先週 ${formatMinutes(previousWeekMinutes)}）`;
    }

    updateProgressControls() {
        if (!this.progressPrevButton || !this.progressNextButton) {
            return;
        }

        if (!this.courseSummaries.length) {
            this.progressPrevButton.disabled = true;
            this.progressNextButton.disabled = true;
            return;
        }

        this.progressPrevButton.disabled = this.activeCourseIndex === 0;
        this.progressNextButton.disabled = this.activeCourseIndex === this.courseSummaries.length - 1;
    }

    updateProgressCircleChart(rate) {
        if (!this.progressCircleCanvas) {
            return;
        }

        const safeRate = Math.max(0, Math.min(100, Number(rate) || 0));
        const data = [safeRate, Math.max(0, 100 - safeRate)];

        if (!this.progressCircleChart) {
            this.progressCircleChart = new Chart(this.progressCircleCanvas, {
                type: 'doughnut',
                data: {
                    labels: ['completed', 'remaining'],
                    datasets: [
                        {
                            data,
                            backgroundColor: ['#ff9100', '#f0f3f8'],
                            borderWidth: 0,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '75%',
                    plugins: {
                        legend: { display: false },
                        tooltip: { enabled: false },
                    },
                },
            });
            return;
        }

        this.progressCircleChart.data.datasets[0].data = data;
        this.progressCircleChart.update();
    }

    updateProgressHistoryChart(entries) {
        if (!this.progressHistoryCanvas) {
            return;
        }

        const labels = entries.map((entry) => {
            const date = new Date(entry.date);
            if (Number.isNaN(date.getTime())) {
                return entry.date;
            }
            const month = `${date.getMonth() + 1}`.padStart(2, '0');
            const day = `${date.getDate()}`.padStart(2, '0');
            return `${month}/${day}`;
        });
        const data = entries.map((entry) => entry.minutes);

        if (!this.progressHistoryChart) {
            this.progressHistoryChart = new Chart(this.progressHistoryCanvas, {
                type: 'bar',
                data: {
                    labels,
                    datasets: [
                        {
                            label: '直近7日の学習記録',
                            data,
                            backgroundColor: '#ffab40',
                            borderRadius: 6,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: { precision: 0 },
                            title: { display: true, text: '分' },
                        },
                        x: {
                            ticks: { maxRotation: 0 },
                        },
                    },
                    plugins: {
                        legend: { display: false },
                    },
                },
            });
            return;
        }

        this.progressHistoryChart.data.labels = labels;
        this.progressHistoryChart.data.datasets[0].data = data;
        this.progressHistoryChart.update();
    }

    renderCalendar(summary) {
        if (!this.progressCalendarContainer || !summary) {
            return;
        }

        const courseId = summary.course_id;
        const months = Array.isArray(summary.calendar) ? summary.calendar : [];

        let state = this.calendarStates.get(courseId);
        if (!state) {
            state = { index: months.length ? months.length - 1 : 0 };
        }

        state.months = months;
        if (months.length === 0) {
            state.index = 0;
        } else if (state.index >= months.length) {
            state.index = months.length - 1;
        } else if (state.index < 0) {
            state.index = 0;
        }

        this.calendarStates.set(courseId, state);
        this.updateCalendarDisplay(summary);
    }

    changeCalendarMonth(delta) {
        const summary = this.courseSummaries[this.activeCourseIndex];
        if (!summary) {
            return;
        }

        const courseId = summary.course_id;
        const state = this.calendarStates.get(courseId);

        if (!state || !Array.isArray(state.months) || !state.months.length) {
            return;
        }

        const nextIndex = state.index + delta;
        if (nextIndex < 0 || nextIndex >= state.months.length) {
            return;
        }

        state.index = nextIndex;
        this.calendarStates.set(courseId, state);
        this.updateCalendarDisplay(summary);
    }

    updateCalendarDisplay(summary) {
        if (!this.progressCalendarContainer) {
            return;
        }

        const courseId = summary.course_id;
        const state = this.calendarStates.get(courseId);
        const months = state && Array.isArray(state.months) ? state.months : [];
        const index = state ? state.index : 0;

        this.progressCalendarContainer.innerHTML = '';

        if (!months.length) {
            this.setCalendarControlsState({ label: '-', hasPrev: false, hasNext: false });
            const emptyMessage = document.createElement('p');
            emptyMessage.classList.add('progress-calendar__empty');
            emptyMessage.textContent = '最近の学習日がまだありません。';
            this.progressCalendarContainer.appendChild(emptyMessage);
            this.renderStreak(summary.streak || null, months, courseId);
            return;
        }

        const month = months[index];
        this.setCalendarControlsState({
            label: month.label || month.month || '-',
            hasPrev: index > 0,
            hasNext: index < months.length - 1,
        });

        const monthElement = this.buildCalendarMonthElement(month);
        this.progressCalendarContainer.appendChild(monthElement);
        this.renderStreak(summary.streak || null, months, courseId);
    }

    setCalendarControlsState({ label, hasPrev, hasNext }) {
        if (this.progressCalendarLabel) {
            this.progressCalendarLabel.textContent = label;
        }
        if (this.progressCalendarPrevButton) {
            this.progressCalendarPrevButton.disabled = !hasPrev;
        }
        if (this.progressCalendarNextButton) {
            this.progressCalendarNextButton.disabled = !hasNext;
        }
    }

    buildCalendarMonthElement(month) {
        const weekdayLabels = ['日', '月', '火', '水', '木', '金', '土'];
        const monthElement = document.createElement('div');
        monthElement.classList.add('progress-calendar__month');

        const weekdaysRow = document.createElement('div');
        weekdaysRow.classList.add('progress-calendar__weekdays');
        weekdayLabels.forEach((label) => {
            const weekdayCell = document.createElement('span');
            weekdayCell.textContent = label;
            weekdaysRow.appendChild(weekdayCell);
        });
        monthElement.appendChild(weekdaysRow);

        const grid = document.createElement('div');
        grid.classList.add('progress-calendar__grid');

        const days = Array.isArray(month.days) ? month.days : [];
        const firstWeekday = days.length ? Math.max(0, Math.min(6, Number(days[0].weekday) || 0)) : 0;

        for (let i = 0; i < firstWeekday; i += 1) {
            const emptyCell = document.createElement('div');
            emptyCell.classList.add('calendar-day', 'calendar-day--empty');
            grid.appendChild(emptyCell);
        }

        days.forEach((day) => {
            const dayCell = document.createElement('div');
            dayCell.classList.add('calendar-day');

            const minutes = Number(day.minutes) || 0;
            const isToday = Boolean(day.is_today);
            const isFuture = Boolean(day.is_future);

            if (minutes > 0) {
                dayCell.classList.add('calendar-day--active');
                dayCell.setAttribute('data-minutes', String(minutes));
                dayCell.title = `${day.date}｜${minutes}分`;
            }

            if (isToday) {
                dayCell.classList.add('calendar-day--today');
            }

            if (isFuture) {
                dayCell.classList.add('calendar-day--future');
            }

            dayCell.setAttribute('data-date', day.date);

            const dateParts = typeof day.date === 'string' ? day.date.split('-') : [];
            const dayNumber = Number.parseInt((dateParts[2] || '0'), 10);
            const numberElement = document.createElement('span');
            numberElement.classList.add('calendar-day__number');
            numberElement.textContent = Number.isFinite(dayNumber) && dayNumber > 0 ? String(dayNumber) : '';
            dayCell.appendChild(numberElement);

            grid.appendChild(dayCell);
        });

        const remainder = grid.children.length % 7;
        if (remainder !== 0) {
            const fillers = 7 - remainder;
            for (let i = 0; i < fillers; i += 1) {
                const emptyCell = document.createElement('div');
                emptyCell.classList.add('calendar-day', 'calendar-day--empty');
                grid.appendChild(emptyCell);
            }
        }

        monthElement.appendChild(grid);
        return monthElement;
    }

    setProgressView(view) {
        const normalized = view === 'calendar' ? 'calendar' : 'summary';
        this.activeProgressView = normalized;

        if (this.progressViewButtons.length) {
            this.progressViewButtons.forEach((button) => {
                button.classList.toggle('is-active', button.dataset.view === normalized);
            });
        }

        if (this.progressPanels.length) {
            this.progressPanels.forEach((panel) => {
                panel.classList.toggle('is-active', panel.dataset.view === normalized);
            });
        }

        if (normalized === 'calendar') {
            const summary = this.courseSummaries[this.activeCourseIndex];
            if (summary) {
                this.renderCalendar(summary);
            } else {
                this.clearCalendarDisplay();
            }
        }
    }

    setSideView(view) {
        const normalized = view === 'weekly' ? 'weekly' : 'recent';
        this.activeSideView = normalized;

        this.sideViewButtons.forEach((button) => {
            const isActive = button.dataset.view === normalized;
            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        this.sidePanels.forEach((panel) => {
            const isActive = panel.dataset.view === normalized;
            panel.classList.toggle('is-active', isActive);
            if (isActive) {
                panel.removeAttribute('hidden');
            } else {
                panel.setAttribute('hidden', 'hidden');
            }
        });
    }

    clearCalendarDisplay() {
        if (this.progressCalendarContainer) {
            this.progressCalendarContainer.innerHTML = '';
            const emptyMessage = document.createElement('p');
            emptyMessage.classList.add('progress-calendar__empty');
            emptyMessage.textContent = '最近の学習日がまだありません。';
            this.progressCalendarContainer.appendChild(emptyMessage);
        }
        if (this.progressCalendarLabel) {
            this.progressCalendarLabel.textContent = '-';
        }
        if (this.progressCalendarPrevButton) {
            this.progressCalendarPrevButton.disabled = true;
        }
        if (this.progressCalendarNextButton) {
            this.progressCalendarNextButton.disabled = true;
        }
        if (this.progressStreakContainer) {
            this.progressStreakContainer.innerHTML = '';
        }
    }

    renderStreak(streak, calendarMonths, courseId) {
        if (!this.progressStreakContainer) {
            return;
        }

        this.progressStreakContainer.innerHTML = '';

        const current = Number(streak && streak.current_days) || 0;
        const longest = Number(streak && streak.longest_days) || 0;
        const months = Array.isArray(calendarMonths) ? calendarMonths : [];
        const state = this.calendarStates.get(courseId);
        const selectedIndex = state && Array.isArray(state.months) ? state.index : (months.length ? months.length - 1 : 0);
        const safeIndex = months.length ? Math.max(0, Math.min(selectedIndex, months.length - 1)) : 0;
        const currentMonth = months.length ? months[safeIndex] : null;

        const activeDaysThisMonth = currentMonth
            ? (currentMonth.days || []).filter((day) => {
                const minutes = Number(day.minutes) || 0;
                return minutes > 0 && !day.is_future;
            }).length
            : 0;

        const monthTarget = 10;
        const monthRemaining = Math.max(0, monthTarget - activeDaysThisMonth);
        const monthHint = (() => {
            if (activeDaysThisMonth === 0) {
                return '今日からスタートしよう';
            }
            if (activeDaysThisMonth >= monthTarget) {
                return '今月の目標を達成！';
            }
            if (monthRemaining <= 2) {
                return `あと${monthRemaining}日で${monthTarget}日達成！`;
            }
            return 'ナイス継続！';
        })();

        const longestHint = (() => {
            if (longest === 0) {
                return 'まずは1日から始めよう';
            }
            if (longest < current) {
                return '記録更新中！';
            }
            if (longest === current && current !== 0) {
                return '最長記録に並びました！';
            }
            return `最長${longest}日を超えよう`;
        })();

        const cards = [
            {
                label: '現在の連続学習',
                value: current,
                unit: '日',
                hint: this.buildStreakHint(current),
            },
            {
                label: '今月の学習日数',
                value: activeDaysThisMonth,
                unit: '日',
                hint: monthHint,
            },
            {
                label: 'これまでの最長',
                value: longest,
                unit: '日',
                hint: longestHint,
            },
        ];

        cards.forEach((card) => {
            const cardElement = document.createElement('div');
            cardElement.classList.add('progress-streak__card');

            const labelElement = document.createElement('span');
            labelElement.classList.add('progress-streak__label');
            labelElement.textContent = card.label;
            cardElement.appendChild(labelElement);

            const valueElement = document.createElement('span');
            valueElement.classList.add('progress-streak__value');

            const valueNumber = document.createElement('strong');
            valueNumber.textContent = String(card.value);
            valueElement.appendChild(valueNumber);

            const valueUnit = document.createElement('span');
            valueUnit.textContent = card.unit;
            valueElement.appendChild(valueUnit);

            cardElement.appendChild(valueElement);

            const hintElement = document.createElement('span');
            hintElement.classList.add('progress-streak__hint');
            hintElement.textContent = card.hint;
            cardElement.appendChild(hintElement);

            this.progressStreakContainer.appendChild(cardElement);
        });
    }

    buildStreakHint(currentStreak) {
        const milestones = [3, 5, 7, 10, 14, 21, 30];
        const nextMilestone = milestones.find((milestone) => milestone > currentStreak);

        if (!nextMilestone) {
            return '素晴らしい継続です！';
        }

        const remaining = nextMilestone - currentStreak;

        if (remaining === 1) {
            return `あと1日で${nextMilestone}日連続！`;
        }

        return `あと${remaining}日で${nextMilestone}日連続！`;
    }

    renderRecentLogs(logs) {
        if (!this.progressRecentList || !this.progressEmptyMessage) {
            return;
        }

        this.progressRecentList.innerHTML = '';

        if (!logs.length) {
            this.progressEmptyMessage.classList.remove('d-none');
            return;
        }

        this.progressEmptyMessage.classList.add('d-none');

        logs.slice(0, 5).forEach((log) => {
            const li = document.createElement('li');

            const title = document.createElement('div');
            title.classList.add('title');
            title.textContent = [log.chapter_name, log.text_name].filter(Boolean).join(' / ') || '―';
            li.appendChild(title);

            const meta = document.createElement('div');
            meta.classList.add('meta');
            const minutes = Number.isFinite(Number(log.minutes)) ? `${Math.max(0, Number(log.minutes))}分` : '―';
            meta.textContent = [log.studied_at || '―', minutes].join(' ｜ ');
            li.appendChild(meta);

            if (log.memo) {
                const memo = document.createElement('div');
                memo.classList.add('memo');
                memo.textContent = log.memo;
                li.appendChild(memo);
            }

            this.progressRecentList.appendChild(li);
        });
    }

    renderUnreportedAlerts(alerts) {
        if (!this.progressAlertsList || !this.progressAlertsEmpty) {
            return;
        }

        this.progressAlertsList.innerHTML = '';
        const hasAlerts = Array.isArray(alerts) && alerts.length > 0;

        if (!hasAlerts) {
            this.progressAlertsEmpty.classList.remove('d-none');
            if (this.progressAlertsContainer) {
                this.progressAlertsContainer.classList.remove('has-alerts');
                this.progressAlertsContainer.classList.remove('show-tooltip');
            }
            if (this.dismissedAlertKeys.size > 0) {
                this.dismissedAlertKeys.clear();
                this.persistDismissedAlertKeys();
            }
            return;
        }

        this.progressAlertsEmpty.classList.add('d-none');
        if (this.progressAlertsContainer) {
            this.progressAlertsContainer.classList.add('has-alerts');
        }

        const rawKeys = new Set();

        alerts.slice(0, 6).forEach((alert) => {
            const key = this.buildAlertKey(alert);
            rawKeys.add(key);

            if (this.dismissedAlertKeys.has(key)) {
                return;
            }

            const item = document.createElement('li');
            item.classList.add('progress-alert-item');
            item.dataset.alertKey = key;

            const dateLabel = document.createElement('div');
            dateLabel.classList.add('progress-alert-item__date');
            dateLabel.textContent = formatNotificationDate(alert.viewed_date);
            item.appendChild(dateLabel);

            const textLabel = document.createElement('div');
            textLabel.classList.add('progress-alert-item__text');
            const parts = [alert.chapter_name, alert.text_name].filter(Boolean);
            textLabel.textContent = parts.length ? parts.join(' / ') : (alert.text_name || 'テキスト未取得');
            item.appendChild(textLabel);

            const hint = document.createElement('div');
            hint.classList.add('progress-alert-item__hint');
            hint.textContent = '学習報告を登録してください。';
            item.appendChild(hint);

            const actions = document.createElement('div');
            actions.classList.add('progress-alert-item__actions');

            const dismissButton = document.createElement('button');
            dismissButton.type = 'button';
            dismissButton.classList.add('progress-alert-item__dismiss');
            dismissButton.setAttribute('aria-label', 'このお知らせを非表示にする');
            dismissButton.innerHTML = '&#128465;'; // trash icon
            dismissButton.addEventListener('click', () => this.handleDismissAlert(key, item));
            actions.appendChild(dismissButton);

            item.appendChild(actions);

            this.progressAlertsList.appendChild(item);
        });

        // remove stale keys that no longer appear
        const updatedDismissed = new Set();
        this.dismissedAlertKeys.forEach((storedKey) => {
            if (rawKeys.has(storedKey)) {
                updatedDismissed.add(storedKey);
            }
        });
        this.dismissedAlertKeys = updatedDismissed;
        this.persistDismissedAlertKeys();

        if (!this.progressAlertsList.childElementCount) {
            this.progressAlertsEmpty.classList.remove('d-none');
            if (this.progressAlertsContainer) {
                this.progressAlertsContainer.classList.remove('has-alerts');
                this.progressAlertsContainer.classList.remove('show-tooltip');
            }
        }
    }

    setProgressEmptyState(isEmpty) {
        if (!this.progressEmptyMessage) {
            return;
        }

        if (isEmpty) {
            this.progressEmptyMessage.textContent = 'まだ学習時間が記録されていません。';
            this.progressEmptyMessage.classList.remove('d-none');
        } else {
            this.progressEmptyMessage.classList.add('d-none');
        }
    }

    async handleSubmit(event) {
        event.preventDefault();

        if (!this.form || this.loading) {
            return;
        }

        const formData = new FormData(this.form);
        const courseId = Number(formData.get('course_id'));
        const textId = Number(formData.get('text_id'));
        const studiedAt = formData.get('studied_at');

        this.setFormDisabled(true);
        this.showMessage('送信中です...', 'info');

        try {
            const response = await window.axios.post(this.endpoints.store, formData);
            this.showMessage('学習時間を登録しました。', 'success');

            const courseSummary = response.data.course_summary;
            let summaryUpdated = false;
            if (courseSummary) {
                const index = this.courseSummaries.findIndex((course) => course.course_id === courseSummary.course_id);
                if (index >= 0) {
                    this.courseSummaries.splice(index, 1, {
                        ...this.courseSummaries[index],
                        ...courseSummary,
                    });
                    this.activeCourseIndex = index;
                } else {
                    this.courseSummaries.push(courseSummary);
                    this.activeCourseIndex = this.courseSummaries.length - 1;
                }
                summaryUpdated = true;
            }

            const removed = this.removeAlertEntryFromSummaries(courseId, textId, studiedAt);
            if (summaryUpdated || removed) {
                this.renderActiveCourse();
            }

            this.resetForm();
        } catch (error) {
            console.error(error);
            if (error.response && error.response.status === 422) {
                const messages = Object.values(error.response.data.errors || {})
                    .flat()
                    .join('\n');
                this.showMessage(messages || '入力内容を確認してください。', 'error');
            } else if (error.response && error.response.status === 403) {
                this.showMessage('選択したコースにはアクセスできません。', 'error');
            } else {
                this.showMessage('学習時間の登録に失敗しました。再度お試しください。', 'error');
            }
        } finally {
            this.setFormDisabled(false);
        }
    }

    removeAlertEntryFromSummaries(courseId, textId, viewedDate) {
        if (!Number.isFinite(courseId) || !Number.isFinite(textId)) {
            return false;
        }

        const normalizedDate = normalizeDateKey(viewedDate);
        if (!normalizedDate) {
            return false;
        }

        const numericCourseId = Number(courseId);
        const numericTextId = Number(textId);
        let updated = false;

        this.courseSummaries = this.courseSummaries.map((summary) => {
            if (Number(summary.course_id) !== numericCourseId) {
                return summary;
            }

            const alerts = Array.isArray(summary.unreported_logs) ? summary.unreported_logs : [];
            const filtered = alerts.filter((alert) => !(Number(alert.course_id) === numericCourseId
                && Number(alert.text_id) === numericTextId
                && normalizeDateKey(alert.viewed_date) === normalizedDate));

            if (filtered.length === alerts.length) {
                return summary;
            }

            updated = true;
            return {
                ...summary,
                unreported_logs: filtered,
            };
        });

        if (!updated) {
            return false;
        }

        const resolvedKey = this.buildAlertKey({
            course_id: numericCourseId,
            text_id: numericTextId,
            viewed_date: normalizedDate,
        });

        if (this.dismissedAlertKeys.has(resolvedKey)) {
            this.dismissedAlertKeys.delete(resolvedKey);
            this.persistDismissedAlertKeys();
        }

        return true;
    }

    async sendDismissAlert(details) {
        if (!details || !this.endpoints.dismiss) {
            return;
        }

        const { courseId, textId, viewedDate } = details;
        if (!Number.isFinite(courseId) || !Number.isFinite(textId) || !viewedDate) {
            return;
        }

        try {
            await window.axios.post(this.endpoints.dismiss, {
                course_id: courseId,
                text_id: textId,
                viewed_date: viewedDate,
            });
        } catch (error) {
            // 非同期通知の失敗はUXに影響しないためログのみ
            // eslint-disable-next-line no-console
            console.error('Failed to dismiss learning alert.', error);
        }
    }

    parseAlertKey(key) {
        if (!key || typeof key !== 'string') {
            return null;
        }

        const [courseId, textId, viewedDate] = key.split('|');
        if (!courseId || !textId || !viewedDate) {
            return null;
        }

        return {
            courseId: Number(courseId),
            textId: Number(textId),
            viewedDate: normalizeDateKey(viewedDate),
        };
    }

    setFormDisabled(disabled) {
        [this.courseSelect, this.chapterSelect, this.textSelect, this.dateInput, this.minutesInput, this.memoInput]
            .filter(Boolean)
            .forEach((element) => {
                element.disabled = disabled;
            });
        if (this.form) {
            const submitButton = this.form.querySelector('button[type="submit"]');
            if (submitButton) {
                submitButton.disabled = disabled;
            }
        }
    }

    resetForm() {
        if (!this.form) {
            return;
        }

        this.minutesInput.value = '';
        this.memoInput.value = '';
        this.setDefaultDate();
        this.form.querySelector('button[type="submit"]').focus();
    }

    showMessage(message, type = 'info') {
        if (!this.messageArea) {
            return;
        }

        this.messageArea.textContent = message;
        this.messageArea.dataset.type = type;
        this.messageArea.removeAttribute('hidden');
    }

    hideMessage() {
        if (!this.messageArea) {
            return;
        }

        this.messageArea.textContent = '';
        this.messageArea.setAttribute('hidden', 'hidden');
    }

    handleDismissAlert(key, item) {
        this.dismissedAlertKeys.add(key);
        this.persistDismissedAlertKeys();

        const details = this.parseAlertKey(key);
        if (details) {
            const removed = this.removeAlertEntryFromSummaries(details.courseId, details.textId, details.viewedDate);
            if (removed) {
                this.renderActiveCourse();
            }
            this.sendDismissAlert(details);
        }

        if (item && item.parentElement) {
            item.parentElement.removeChild(item);
        }

        if (!this.progressAlertsList || !this.progressAlertsEmpty) {
            return;
        }

        if (!this.progressAlertsList.childElementCount) {
            this.progressAlertsEmpty.classList.remove('d-none');
            if (this.progressAlertsContainer) {
                this.progressAlertsContainer.classList.remove('has-alerts');
                this.progressAlertsContainer.classList.remove('show-tooltip');
            }
        }
    }

    resolveUserId() {
        const sectionUserId = this.progressSection && this.progressSection.dataset
            ? this.progressSection.dataset.userId
            : null;
        if (sectionUserId && sectionUserId.trim() !== '') {
            return sectionUserId;
        }

        const buttonUserId = this.toggleButton && this.toggleButton.dataset
            ? this.toggleButton.dataset.userId
            : null;
        if (buttonUserId && buttonUserId.trim() !== '') {
            return buttonUserId;
        }

        if (document.body && document.body.dataset && document.body.dataset.userId
            && document.body.dataset.userId.trim() !== '') {
            return document.body.dataset.userId;
        }

        return null;
    }

    buildDismissedAlertsStorageKey() {
        const baseKey = 'learning-time-dismissed-alerts';
        if (this.userId && this.userId.trim() !== '') {
            return `${baseKey}:${this.userId}`;
        }
        return baseKey;
    }

    loadDismissedAlertKeys() {
        if (typeof window === 'undefined' || !window.localStorage) {
            return new Set();
        }

        try {
            const raw = window.localStorage.getItem(this.dismissedAlertsStorageKey);
            if (!raw) {
                return new Set();
            }

            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) {
                return new Set();
            }

            return new Set(parsed.filter((value) => typeof value === 'string' && value.length > 0));
        } catch (error) {
            return new Set();
        }
    }

    persistDismissedAlertKeys() {
        if (typeof window === 'undefined' || !window.localStorage) {
            return;
        }

        try {
            const values = Array.from(this.dismissedAlertKeys);
            if (values.length === 0) {
                window.localStorage.removeItem(this.dismissedAlertsStorageKey);
                return;
            }

            window.localStorage.setItem(this.dismissedAlertsStorageKey, JSON.stringify(values));
        } catch (error) {
            // localStorage might be unavailable (e.g. private mode); ignore errors
        }
    }

    buildAlertKey(alert) {
        if (!alert) {
            return '';
        }
        const courseId = alert.course_id ?? '';
        const textId = alert.text_id ?? '';
        const viewedDate = alert.viewed_date ?? '';
        return [courseId, textId, viewedDate].join('|');
    }
}
